import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, formatApiError, getStoredToken, setStoredToken } from "@/lib/api";

/**
 * Auth states:
 *   status === "loading"  -> we're checking existing token
 *   status === "authenticated" -> user is defined
 *   status === "unauthenticated" -> no valid session
 */
const AuthContext = createContext(null);

// Storage key holding the ORIGINAL admin session while an admin is
// impersonating a broker. `exitImpersonation()` restores from here.
const IMP_KEY = "mb.imp.parent";

function readParentSession() {
  try {
    const raw = localStorage.getItem(IMP_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function writeParentSession(v) {
  try {
    if (v == null) localStorage.removeItem(IMP_KEY);
    else localStorage.setItem(IMP_KEY, JSON.stringify(v));
  } catch { /* silent */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading");
  const [isImpersonating, setIsImpersonating] = useState(false);

  const refreshMe = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setUser(null);
      setStatus("unauthenticated");
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setStatus("authenticated");
      return data;
    } catch (err) {
      setStoredToken(null);
      setUser(null);
      setStatus("unauthenticated");
      return null;
    }
  }, []);

  // Sync impersonation flag on mount and whenever localStorage changes.
  useEffect(() => {
    setIsImpersonating(!!readParentSession());
    const onStorage = (e) => {
      if (e.key === IMP_KEY) setIsImpersonating(!!readParentSession());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setStoredToken(data.access_token);
      setUser(data.user);
      setStatus("authenticated");
      return { ok: true, user: data.user };
    } catch (err) {
      return { ok: false, error: formatApiError(err, "Login failed") };
    }
  }, []);

  const register = useCallback(async (formData) => {
    try {
      const { data } = await api.post("/auth/register", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStoredToken(data.access_token);
      setUser(data.user);
      setStatus("authenticated");
      return { ok: true, user: data.user };
    } catch (err) {
      return { ok: false, error: formatApiError(err, "Registration failed") };
    }
  }, []);

  const logout = useCallback(() => {
    setStoredToken(null);
    setUser(null);
    setStatus("unauthenticated");
    writeParentSession(null);
    setIsImpersonating(false);
  }, []);

  /**
   * Adopt an impersonation token returned by
   * POST /api/admin/impersonate/{user_id}. Stashes the current admin
   * JWT + user under `IMP_KEY` so we can restore it later.
   */
  const adoptImpersonation = useCallback((payload) => {
    const currentToken = getStoredToken();
    if (currentToken && user?.role) {
      writeParentSession({ token: currentToken, user });
    }
    setStoredToken(payload.access_token);
    setUser(payload.target_user);
    setStatus("authenticated");
    setIsImpersonating(true);
  }, [user]);

  const exitImpersonation = useCallback(() => {
    const parent = readParentSession();
    writeParentSession(null);
    setIsImpersonating(false);
    if (parent?.token) {
      setStoredToken(parent.token);
      setUser(parent.user);
      setStatus("authenticated");
      window.location.assign("/control-panel-x9");
    } else {
      logout();
      window.location.assign("/control-panel-x9/login");
    }
  }, [logout]);

  const value = useMemo(
    () => ({
      user,
      status,
      login,
      register,
      logout,
      refreshMe,
      refreshUser: refreshMe,
      adoptImpersonation,
      exitImpersonation,
      isImpersonating,
    }),
    [user, status, login, register, logout, refreshMe, adoptImpersonation, exitImpersonation, isImpersonating]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
